import { useEffect, useRef, useState } from "react";
import { Mic, Square, Upload, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { FormDialog } from "@/components/kids/kidsUi";
import { useHub } from "@/components/AppShell";

const cls = "q-input min-h-[44px] w-full";
const DAY = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MISSED = "Sorry, I didn't catch that. Try again.";

function save(hubId, c) {
  const h = `/organizations/${hubId}`;
  if (c.type === "list_item") return api.post(`${h}/lists/${c.list_id}/items`, { items: [{ name: c.name, qty: c.qty || null }], force: true });
  if (c.type === "chore") return api.post(`${h}/chores`, { title: c.title, kid_ids: [c.kid_id], schedule: c.schedule, days: c.days, icon: "⭐", reward: 0 });
  return api.post(`${h}/calendar/events`, { title: c.title, start: c.start, end: c.end, all_day: !!c.all_day, category: "Family", reminder: c.type === "reminder" ? "0m" : null });
}

function ActionCard({ c, set, onSave, onDiscard }) {
  const f = (k, label, type = "text") => <input type={type} aria-label={label} className={cls} value={c[k] || ""} onChange={(e) => set({ ...c, [k]: e.target.value })} data-testid={`voice-card-${c.id}-${k}`} />;
  const title = { list_item: `Add to ${c.list_name || "list"}`, chore: `Chore for ${c.kid_name || "?"}`, reminder: "Reminder", event: "Calendar event" }[c.type];
  return (
    <li className="q-card space-y-2 p-3" data-testid={`voice-card-${c.id}`}>
      <div className="text-[10px] font-black uppercase tracking-[0.16em] text-q-faint" data-testid={`voice-card-${c.id}-type`}>{title}</div>
      {c.type === "list_item" && <div className="grid grid-cols-[1fr_5rem] gap-2">{f("name", "Item")}{f("qty", "Qty")}</div>}
      {c.type === "chore" && <>{f("title", "Chore")}<div className="text-xs text-q-muted" data-testid={`voice-card-${c.id}-schedule`}>{c.schedule === "weekly" ? `Every ${c.days.map((d) => DAY[d]).join(", ")}` : c.schedule}</div></>}
      {["reminder", "event"].includes(c.type) && <>{f("title", "Title")}<div className="grid grid-cols-2 gap-2">{f("start", "Start", "datetime-local")}{c.type === "event" && f("end", "End", "datetime-local")}</div></>}
      {!c.allowed && <p className="text-sm font-semibold text-amber-300" data-testid={`voice-card-${c.id}-reason`}>{c.reason}</p>}
      <div className="flex gap-2">
        <button className="q-btn q-btn-primary min-h-[44px] flex-1" disabled={!c.allowed} onClick={onSave} data-testid={`voice-card-${c.id}-save`}><Check className="h-4 w-4" /> Save</button>
        <button className="q-btn q-btn-ghost min-h-[44px]" onClick={onDiscard} data-testid={`voice-card-${c.id}-discard`}><X className="h-4 w-4" /> Discard</button>
      </div>
    </li>
  );
}

function useRecorder(onAudio, onDenied) {
  const [on, setOn] = useState(false);
  const [level, setLevel] = useState(0);
  const [err, setErr] = useState("");
  const r = useRef({});
  const stop = () => { r.current.rec?.state === "recording" && r.current.rec.stop(); };
  const start = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      const ac = new AudioContext();
      const an = ac.createAnalyser();
      ac.createMediaStreamSource(stream).connect(an);
      const buf = new Uint8Array(an.frequencyBinCount);
      const tick = setInterval(() => { an.getByteFrequencyData(buf); setLevel(Math.min(1, buf.reduce((a, b) => a + b, 0) / buf.length / 80)); }, 80);
      const cap = setTimeout(stop, 30000);
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = () => { clearInterval(tick); clearTimeout(cap); stream.getTracks().forEach((t) => t.stop()); ac.close(); setOn(false); setLevel(0); onAudio(new File(chunks, "voice.webm", { type: "audio/webm" })); };
      r.current = { rec };
      rec.start();
      setOn(true);
    } catch (e) { setErr("Microphone is off. Allow it in your browser settings, or type instead."); onDenied?.(); }
  };
  useEffect(() => () => stop(), []);
  return { on, level, err, start, stop };
}

export function VoiceButton({ hubId, screen, listId, onSaved, label = "Voice" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [text, setText] = useState("");
  const [msg, setMsg] = useState("");
  const send = async (audio) => {
    setBusy(true); setMsg(""); setRes(null);
    const fd = new FormData();
    if (audio) fd.append("audio", audio); else fd.append("text", text);
    fd.append("screen", screen); fd.append("list_id", listId || "");
    try { setRes((await api.post(`/organizations/${hubId}/voice/command`, fd)).data); } catch (e) { setMsg(e.response?.status === 422 ? MISSED : errMsg(e)); }
    setBusy(false);
  };
  const textRef = useRef(null);
  const rec = useRecorder(send, () => setTimeout(() => textRef.current?.focus(), 0));
  const { hub } = useHub();
  const setCard = (c) => setRes((r) => ({ ...r, cards: r.cards.map((x) => (x.id === c.id ? c : x)) }));
  const drop = (c) => setRes((r) => ({ ...r, cards: r.cards.filter((x) => x.id !== c.id) }));
  const saveOne = (c) => save(hubId, c).then(() => { toast.success("Saved"); drop(c); onSaved?.(); }).catch((e) => toast.error(errMsg(e)));
  const close = () => { rec.stop(); setOpen(false); setRes(null); setMsg(""); setText(""); };
  if (!hub.my_access?.voice) return null;
  return (
    <>
      <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => setOpen(true)} aria-label="Voice command" data-testid={`voice-btn-${screen}`}><Mic className="h-4 w-4" /> {label}</button>
      {open && <FormDialog open onClose={close} title="Say it" description="Tap the mic and speak (up to 30 seconds). Nothing is saved until you tap Save." testid="voice-dialog">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            {!rec.on ? <button className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 text-white transition-transform duration-150 hover:scale-105 disabled:opacity-50" disabled={busy} onClick={rec.start} aria-label="Start recording" data-testid="voice-record"><Mic className="h-7 w-7" /></button>
              : <button className="flex h-16 w-16 items-center justify-center rounded-full bg-q-text text-q-bg" onClick={rec.stop} aria-label="Stop" data-testid="voice-stop"><Square className="h-6 w-6" /></button>}
            <div className="h-3 flex-1 overflow-hidden rounded-full bg-q-raised" data-testid="voice-level"><div className="h-full rounded-full bg-red-400 transition-[width] duration-75" style={{ width: `${Math.round(rec.level * 100)}%` }} /></div>
          </div>
          {rec.on && <p className="text-sm font-semibold text-red-300" data-testid="voice-listening">Listening…</p>}
          {rec.err && <p className="text-sm font-semibold text-amber-300" data-testid="voice-mic-error">{rec.err}</p>}
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (text.trim()) send(null); }}>
            <input ref={textRef} className={cls} placeholder="Or type it" value={text} onChange={(e) => setText(e.target.value)} data-testid="voice-text" />
            <button className="q-btn q-btn-secondary min-h-[44px] shrink-0" disabled={busy || !text.trim()} data-testid="voice-text-send">Go</button>
          </form>
          <label className="q-btn q-btn-ghost min-h-[44px] w-full cursor-pointer text-xs" data-testid="voice-file-label"><Upload className="h-4 w-4" /> Use an audio file
            <input type="file" accept="audio/*" className="hidden" onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) send(f); }} data-testid="voice-file-input" /></label>
          {busy && <p className="text-sm text-q-muted" data-testid="voice-busy">Working it out…</p>}
          {msg && <p className="text-sm font-semibold text-amber-300" data-testid="voice-error">{msg}</p>}
          {res && <>
            <p className="text-sm text-q-muted">Heard: <span className="text-q-text" data-testid="voice-transcript">“{res.transcript}”</span></p>
            <ul className="max-h-[45vh] space-y-2 overflow-y-auto" data-testid="voice-cards">{res.cards.map((c) => <ActionCard key={c.id} c={c} set={setCard} onSave={() => saveOne(c)} onDiscard={() => drop(c)} />)}</ul>
            {res.cards.filter((c) => c.allowed).length > 1 && <button className="q-btn q-btn-primary min-h-[44px] w-full" data-testid="voice-save-all"
              onClick={async () => { for (const c of res.cards.filter((x) => x.allowed)) await saveOne(c); }}>Save all</button>}
            {!res.cards.length && <p className="text-sm text-q-muted" data-testid="voice-done">All done.</p>}
          </>}
        </div>
      </FormDialog>}
    </>
  );
}
